import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ScheduleSlot, ScheduleSlotUser } from '@festpix/types';

export interface CreateScheduleSlotParams {
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  assignedUsers: ScheduleSlotUser[];
}

export interface UpdateScheduleSlotParams {
  startTime?: string;
  endTime?: string;
  assignedUsers?: ScheduleSlotUser[];
}

const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

function validateTime(time: string): void {
  if (!TIME_REGEX.test(time)) {
    throw new Error('Formato de horário inválido. Use HH:MM');
  }
}

/**
 * Get all schedule slots for a specific booth, ordered by start time
 */
export async function getBoothScheduleSlots(boothId: string): Promise<ScheduleSlot[]> {
  const slotsQuery = query(
    collection(db, 'scheduleSlots'),
    where('boothId', '==', boothId),
    orderBy('startTime', 'asc')
  );

  const snapshot = await getDocs(slotsQuery);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      boothId: data.boothId,
      startTime: data.startTime,
      endTime: data.endTime,
      assignedUsers: data.assignedUsers || [],
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    };
  }) as ScheduleSlot[];
}

/**
 * Create a new schedule slot for a booth
 */
export async function createScheduleSlot(
  boothId: string,
  params: CreateScheduleSlotParams
): Promise<ScheduleSlot> {
  const { startTime, endTime, assignedUsers } = params;

  validateTime(startTime);
  validateTime(endTime);

  if (startTime >= endTime) {
    throw new Error('Horário de início deve ser anterior ao horário de término');
  }

  if (!assignedUsers || assignedUsers.length === 0) {
    throw new Error('Selecione pelo menos um usuário');
  }

  const slotRef = await addDoc(collection(db, 'scheduleSlots'), {
    boothId,
    startTime,
    endTime,
    assignedUsers,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    id: slotRef.id,
    boothId,
    startTime,
    endTime,
    assignedUsers,
  };
}

/**
 * Update an existing schedule slot
 */
export async function updateScheduleSlot(
  slotId: string,
  params: UpdateScheduleSlotParams
): Promise<void> {
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (params.startTime !== undefined) {
    validateTime(params.startTime);
    updates.startTime = params.startTime;
  }

  if (params.endTime !== undefined) {
    validateTime(params.endTime);
    updates.endTime = params.endTime;
  }

  if (params.startTime !== undefined && params.endTime !== undefined) {
    if (params.startTime >= params.endTime) {
      throw new Error('Horário de início deve ser anterior ao horário de término');
    }
  }

  if (params.assignedUsers !== undefined) {
    if (params.assignedUsers.length === 0) {
      throw new Error('Selecione pelo menos um usuário');
    }
    updates.assignedUsers = params.assignedUsers;
  }

  await updateDoc(doc(db, 'scheduleSlots', slotId), updates);
}

/**
 * Delete a schedule slot
 */
export async function deleteScheduleSlot(slotId: string): Promise<void> {
  await deleteDoc(doc(db, 'scheduleSlots', slotId));
}
